import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Calendar, Filter, History, RefreshCw, Search } from 'lucide-react';
import { describePortalError, getPortalActivity, getPortalPayPeriodsAround } from '../../../api/portal';
import { usePortalData } from './PortalContext';
import ActivityRow from './ActivityRow';
import { ACTIVITY_SOURCE_ICON } from './SrcChip';
import {
  ActivityFilter,
  DEFAULT_RANGE,
  dayBefore,
  filterActivity,
  groupByDay,
  isFiltered,
  loadedWeeksLabel,
  mergeActivity,
  NO_FILTER,
  oldestOfPage,
  periodBefore,
  RANGE_OPTIONS,
  RangeWeeks,
  SOURCE_FILTER_LABEL,
  SOURCE_FILTERS,
  toggleSource,
  TYPE_FILTERS,
  ActivityTypeFilter,
} from './activityModel';
import { mergePeriods } from './timesheetModel';
import type { PortalErrorInfo } from './portalErrors';
import type { PortalActivityItem, PortalPayPeriod } from './portalTypes';
import './employeePortal.css';
import './myTimesheet.css';
import './myTimeOff.css';
import './myActivity.css';

/**
 * Employee Time Portal — My Activity (P-TAS-20 Phase 3, App-4; handoff §9, screenshot 44).
 * GET /portal/activity?payPeriodID=&weeks= — every punch, edit, submit, approval, unlock and
 * time-off event on the employee's record, newest first, grouped by day. Type / source /
 * search filters are client-side over what's loaded. The range (1/2/4/8 weeks) is the
 * `weeks` of each page; "Load older" anchors the next page on the week before the oldest
 * one read. Device / browser / IP aren't stored, so rows don't show them.
 * Fixed-height states (D7): loading / empty / error 320px, pager 58px.
 */

interface PageCursor {
  /** Start of the page's anchor (newest) week. */
  anchorStartYmd: string;
  /** The weeks the page read, newest first. */
  payPeriodIDs: number[];
}

type OlderState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'end' } | { kind: 'error'; message: string };

const MyActivityPage: React.FC = () => {
  const { zone, currentPeriod, periods: ctxPeriods, reportSessionExpired, onSessionRestored } = usePortalData();

  const [range, setRange] = useState<RangeWeeks>(DEFAULT_RANGE);
  const [filter, setFilter] = useState<ActivityFilter>(NO_FILTER);
  const [items, setItems] = useState<PortalActivityItem[]>([]);
  const [weeksRead, setWeeksRead] = useState<number[]>([]);
  const [firstState, setFirstState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [firstError, setFirstError] = useState<PortalErrorInfo | null>(null);
  const [older, setOlder] = useState<OlderState>({ kind: 'idle' });
  const [extraPeriods, setExtraPeriods] = useState<PortalPayPeriod[]>([]);
  const cursor = useRef<PageCursor | null>(null);
  const seq = useRef(0);

  const knownPeriods = useMemo(() => mergePeriods(ctxPeriods, extraPeriods), [ctxPeriods, extraPeriods]);
  const knownRef = useRef(knownPeriods);
  knownRef.current = knownPeriods;

  const onError = useCallback((err: unknown): PortalErrorInfo => {
    const info = describePortalError(err);
    if (info.kind === 'sessionExpired') reportSessionExpired();
    return info;
  }, [reportSessionExpired]);

  /* ------------------------------------------------ first page (this week and range - 1 before) */
  // A ref, so the period loading after the first fetch doesn't refetch the feed.
  const currentStartRef = useRef<string | null>(null);
  currentStartRef.current = currentPeriod?.startYmd ?? null;
  const loadFirst = useCallback(async (weeks: RangeWeeks) => {
    const mine = ++seq.current;
    setFirstState('loading');
    setFirstError(null);
    setOlder({ kind: 'idle' });
    try {
      const res = await getPortalActivity({ weeks });
      if (mine !== seq.current) return;
      const anchorID = res.payPeriodIDs[0];
      const anchor = knownRef.current.find((p) => p.payPeriodID === anchorID);
      cursor.current = { anchorStartYmd: anchor?.startYmd ?? '', payPeriodIDs: res.payPeriodIDs };
      setItems(res.items);
      setWeeksRead(res.payPeriodIDs);
      setFirstState('ready');
      if (res.payPeriodIDs.length < weeks) setOlder({ kind: 'end' });
    } catch (err) {
      if (mine !== seq.current) return;
      setFirstError(onError(err));
      setFirstState('error');
    }
  }, [onError]);

  useEffect(() => { void loadFirst(range); }, [range, loadFirst]);
  useEffect(() => onSessionRestored(() => { void loadFirst(range); }), [onSessionRestored, loadFirst, range]);

  /* ------------------------------------------------ load older */
  const periodsAround = useCallback(async (ymd: string): Promise<PortalPayPeriod[]> => {
    const fetched = await getPortalPayPeriodsAround(ymd);
    setExtraPeriods((x) => mergePeriods(x, fetched));
    return mergePeriods(knownRef.current, fetched);
  }, []);

  const loadOlder = useCallback(async () => {
    const page = cursor.current;
    if (!page || older.kind === 'loading' || older.kind === 'end') return;
    // The first page anchors on this week; its start may only be known once the shell loaded.
    const anchorStartYmd = page.anchorStartYmd || currentStartRef.current;
    if (!anchorStartYmd) return;
    const mine = seq.current;
    setOlder({ kind: 'loading' });
    try {
      const { id, guessYmd } = oldestOfPage({ startYmd: anchorStartYmd }, page.payPeriodIDs);
      let known = knownRef.current;
      let oldest: Pick<PortalPayPeriod, 'startYmd'> | undefined = known.find((p) => p.payPeriodID === id);
      if (!oldest) {
        known = await periodsAround(guessYmd);
        oldest = known.find((p) => p.payPeriodID === id) ?? { startYmd: guessYmd };
      }
      let before = periodBefore(known, oldest);
      if (!before) {
        known = await periodsAround(dayBefore(oldest));
        before = periodBefore(known, oldest);
      }
      if (mine !== seq.current) return;
      if (!before) {
        setOlder({ kind: 'end' });
        return;
      }
      const res = await getPortalActivity({ payPeriodID: before.payPeriodID, weeks: range });
      if (mine !== seq.current) return;
      cursor.current = { anchorStartYmd: before.startYmd, payPeriodIDs: res.payPeriodIDs };
      setItems((x) => mergeActivity(x, res.items));
      setWeeksRead((x) => [...x, ...res.payPeriodIDs.filter((p) => !x.includes(p))]);
      setOlder(res.payPeriodIDs.length < range ? { kind: 'end' } : { kind: 'idle' });
    } catch (err) {
      if (mine !== seq.current) return;
      const info = onError(err);
      if (info.code === 'PAY_PERIOD_NOT_FOUND') setOlder({ kind: 'end' });
      else setOlder({ kind: 'error', message: info.message });
    }
  }, [older.kind, range, periodsAround, onError]);

  /* ------------------------------------------------ derived */
  const shown = useMemo(() => filterActivity(items, filter), [items, filter]);
  const days = useMemo(() => groupByDay(shown, zone), [shown, zone]);
  const filtered = isFiltered(filter);

  const setType = (type: ActivityTypeFilter) => setFilter((f) => ({ ...f, type }));
  const setQuery = (query: string) => setFilter((f) => ({ ...f, query }));

  /* ------------------------------------------------ render */
  const stateBox = (Icon: typeof History, title: string, body: React.ReactNode, action?: React.ReactNode) => (
    <div className="card act-state">
      <div className="soon">
        <div className="eic"><Icon /></div>
        <div className="et">{title}</div>
        {body ? <p>{body}</p> : null}
        {action}
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (firstState === 'loading') {
    body = stateBox(History, 'Loading your activity…', null);
  } else if (firstState === 'error') {
    body = stateBox(AlertTriangle, "Couldn't load your activity", firstError?.message ?? '', (
      <button type="button" className="btn" onClick={() => void loadFirst(range)}><RefreshCw />Try again</button>
    ));
  } else if (days.length === 0) {
    body = filtered
      ? stateBox(Filter, 'Nothing matches these filters', 'Try another type or source, or load older weeks.', (
        <button type="button" className="btn" onClick={() => setFilter(NO_FILTER)}>Clear filters</button>
      ))
      : stateBox(History, 'No activity yet', `Nothing was recorded on your time in the ${weeksRead.length <= 1 ? 'week' : `${weeksRead.length} weeks`} shown. Load older weeks to look further back.`);
  } else {
    body = days.map((d) => (
      <React.Fragment key={d.ymd}>
        <div className="section-label"><Calendar />{d.label}<span className="cnt">{d.items.length === 1 ? '1 event' : `${d.items.length} events`}</span></div>
        <div className="card"><div className="audit">
          {d.items.map((item) => <ActivityRow key={item.id} item={item} zone={zone} />)}
        </div></div>
      </React.Fragment>
    ));
  }

  const ready = firstState === 'ready';
  let pagerMsg: React.ReactNode = ready ? `${loadedWeeksLabel(weeksRead.length)} · ${items.length === 1 ? '1 event' : `${items.length} events`}` : ' ';
  let pagerAction: React.ReactNode = null;
  if (ready) {
    switch (older.kind) {
      case 'loading':
        pagerAction = <button type="button" className="btn" disabled><RefreshCw />Loading…</button>;
        break;
      case 'end':
        pagerMsg = `${loadedWeeksLabel(weeksRead.length)} · that's everything on record`;
        pagerAction = <button type="button" className="btn" disabled>No older activity</button>;
        break;
      case 'error':
        pagerMsg = <span className="msg err">{older.message}</span>;
        pagerAction = <button type="button" className="btn" onClick={() => void loadOlder()}><RefreshCw />Try again</button>;
        break;
      default:
        pagerAction = (
          <button type="button" className="btn" onClick={() => void loadOlder()}>
            Load older{range === 1 ? ' week' : ` ${range} weeks`}
          </button>
        );
    }
  } else {
    pagerAction = <button type="button" className="btn" disabled>Load older</button>;
  }

  return (
    <div className="etp">
      <div className="page-head">
        <div className="ph-ic"><History /></div>
        <div className="ph-tx">
          <h1>My Activity</h1>
          <div className="ph-sub">Every punch, edit, submission and approval on your record — where it came from, who did it, and what changed</div>
        </div>
        <div className="ph-actions">
          <button type="button" className="btn" disabled={firstState === 'loading'} onClick={() => void loadFirst(range)}>
            <RefreshCw />Refresh
          </button>
        </div>
      </div>

      <div className="act-tools">
        <div className="toolbar">
          <div className="searchwrap">
            <Search />
            <input type="text" placeholder="Search activity" value={filter.query} onChange={(e) => setQuery(e.target.value)} aria-label="Search activity" />
          </div>
          <label className="inline-field">Type
            <select className="select" value={filter.type} onChange={(e) => setType(e.target.value as ActivityTypeFilter)}>
              {TYPE_FILTERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label className="inline-field">Range
            <select className="select" value={range} onChange={(e) => setRange(Number(e.target.value) as RangeWeeks)}>
              {RANGE_OPTIONS.map((w) => <option key={w} value={w}>{w === 1 ? 'This week' : `Last ${w} weeks`}</option>)}
            </select>
          </label>
          <span className="grow" />
          <span className="pill neutral count">{ready ? (shown.length === 1 ? '1 event' : `${shown.length} events`) : '— events'}</span>
        </div>
        <div className="srcfilter" role="group" aria-label="Source">
          <span className="lab">Source</span>
          <button type="button" className={filter.sources.length === 0 ? 'on' : ''} aria-pressed={filter.sources.length === 0}
            onClick={() => setFilter((f) => ({ ...f, sources: [] }))}>All sources</button>
          {SOURCE_FILTERS.map((s) => {
            const Icon = ACTIVITY_SOURCE_ICON[s];
            const on = filter.sources.includes(s);
            return (
              <button key={s} type="button" className={on ? 'on' : ''} aria-pressed={on}
                title={s === 'employee' ? 'Submits and time-off requests you made (web or mobile)' : undefined}
                onClick={() => setFilter((f) => ({ ...f, sources: toggleSource(f.sources, s) }))}>
                <Icon />{SOURCE_FILTER_LABEL[s]}
              </button>
            );
          })}
        </div>
      </div>

      {body}

      <div className="pager act-pager">
        <span className="grow msg">{pagerMsg}</span>
        {pagerAction}
      </div>
    </div>
  );
};

export default MyActivityPage;
